import { useMemo, useState } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

/**
 * Side panel «Колонки таблицы» (Чат 4, правка №15). [Claude] 2026-11-02
 * Visible columns: drag (or ↑/↓) to reorder, untick to hide. Hidden columns: tick to show.
 * Every change is applied immediately through onChange(ids); pinned columns can move but not hide.
 */
function VisibleRow({ column, index, count, onMove, onToggle, lockHide = false }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: column.id });
    const style = { transform: CSS.Transform.toString(transform), transition };
    return <li ref={setNodeRef} style={style} data-testid="column-settings-visible" data-column={column.id}
        className={`flex items-center gap-2 rounded-lg border px-2 py-1.5 text-sm ${isDragging ? "border-amber-400 bg-amber-50 shadow" : "border-slate-200 bg-white"}`}>
        <span {...attributes} {...listeners} aria-label={`Перетащить «${column.label}»`}
            className="cursor-grab select-none px-1 text-slate-400">⋮⋮</span>
        <label className="flex min-w-0 flex-1 items-center gap-2">
            <input type="checkbox" checked disabled={column.pinned || lockHide} onChange={() => onToggle(column.id)} aria-label={`Скрыть «${column.label}»`} title={lockHide ? "Должна остаться хотя бы одна колонка, кроме закреплённой" : undefined} />
            <span className="truncate">{column.label}</span>
            {column.pinned ? <span className="text-xs text-slate-400">всегда видна</span> : null}
        </label>
        <span className="flex gap-0.5">
            <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} aria-label={`Выше: ${column.label}`} className="rounded px-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↑</button>
            <button type="button" disabled={index === count - 1} onClick={() => onMove(index, 1)} aria-label={`Ниже: ${column.label}`} className="rounded px-1.5 text-slate-500 hover:bg-slate-100 disabled:opacity-30">↓</button>
        </span>
    </li>;
}

export default function ColumnSettingsPanel({ catalog, visibleIds, onChange, onReset, onClose }) {
    const [query, setQuery] = useState("");
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const byId = useMemo(() => new Map(catalog.map((c) => [c.id, c])), [catalog]);
    const movable = visibleIds;
    const hideable = visibleIds.filter((id) => !byId.get(id)?.pinned);
    const hidden = catalog.filter((c) => !visibleIds.includes(c.id))
        .filter((c) => !query.trim() || c.label.toLocaleLowerCase("ru").includes(query.trim().toLocaleLowerCase("ru")));

    const emit = (ids) => onChange(ids);
    const move = (index, delta) => emit(arrayMove(movable, index, index + delta));
    const toggle = (id) => {
        if (movable.includes(id)) {
            if (byId.get(id)?.pinned || hideable.length === 1) return; // an empty choice would mean «defaults»
            emit(movable.filter((x) => x !== id));
        } else emit([...movable, id]);
    };
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const from = movable.indexOf(active.id);
        const to = movable.indexOf(over.id);
        if (from < 0 || to < 0) return;
        emit(arrayMove(movable, from, to));
    };

    return <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/20" onClick={onClose} data-testid="column-settings">
        <aside className="flex h-full w-full max-w-sm flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <div><h2 className="text-base font-bold text-slate-900">Колонки таблицы</h2><p className="text-xs text-slate-500">Сохраняется за вашей учётной записью</p></div>
                <button type="button" onClick={onClose} className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100" aria-label="Закрыть">✕</button>
            </header>
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
                <section>
                    <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Показаны · {visibleIds.length}</h3>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={movable} strategy={verticalListSortingStrategy}>
                            <ul className="space-y-1.5">
                                {movable.map((id, index) => <VisibleRow key={id} column={byId.get(id)} index={index} count={movable.length} onMove={move} onToggle={toggle} lockHide={hideable.length === 1} />)}
                            </ul>
                        </SortableContext>
                    </DndContext>
                </section>
                <section>
                    <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-500">Можно добавить · {catalog.length - visibleIds.length}</h3>
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Найти колонку" className="mb-2 w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-amber-400" data-testid="column-settings-search" />
                    <ul className="space-y-1">
                        {hidden.map((c) => <li key={c.id} data-testid="column-settings-hidden">
                            <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50">
                                <input type="checkbox" checked={false} onChange={() => toggle(c.id)} aria-label={`Показать «${c.label}»`} />{c.label}
                            </label>
                        </li>)}
                        {!hidden.length ? <li className="px-2 text-sm text-slate-400">Все колонки уже показаны</li> : null}
                    </ul>
                </section>
            </div>
            <footer className="flex justify-between border-t border-slate-100 px-4 py-3">
                <button type="button" onClick={onReset} className="text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="column-settings-reset">Сбросить как было</button>
                <button type="button" onClick={onClose} className="rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white">Готово</button>
            </footer>
        </aside>
    </div>;
}
